import { useState } from 'react'
import { useIntl } from 'react-intl'

import { Button } from 'DesignSystem'

import useIntlShort from 'Hooks/useIntlShort.hook'

import { ModalS, HeaderS, BodyS, AckS, FullTermsS, FooterS } from './TermsModal.styled'

interface TermsModalPropsI {
  isOpen: boolean,
  appName: string,
  onCancel: () => void,
  onAccept: () => void
}

/**
 * Termos de uso do MCP.
 *
 * Aceitar aqui é o que marca o checkbox da tela de consentimento — o de dentro
 * é o reconhecimento explícito, então ele destrava o botão.
 *
 * A modal do DesignSystem fica sempre montada (o `isOpen` só controla opacidade
 * e pointer-events), por isso o reconhecimento é resetado ao cancelar em vez de
 * depender da desmontagem.
 */

const MCP_SERVER_URL = 'https://mcp.neodash.ai/mcp'

const TermsModal = (props: TermsModalPropsI) => {
  const { isOpen, appName, onCancel, onAccept } = props

  const intl = useIntlShort()
  const { formatMessage } = useIntl()

  const [acknowledged, setAcknowledged] = useState(false)

  const cancel = () => {
    setAcknowledged(false)
    onCancel()
  }

  const termsUrl = formatMessage({ id: 'https://neodash.ai/mcp-termo-de-uso' })
  const privacyUrl = formatMessage({ id: 'http://neodash.ai/privacidade-seguranca' })

  return (
    <ModalS isOpen={isOpen}>
      <HeaderS className="fw-bold">{intl('Termos de uso - NeoDash MCP')}</HeaderS>

      <BodyS>
        <p className="body1 fw-bold">
          {intl('Você está conectando um assistente de IA à sua conta NeoDash.')}
        </p>
        <p className="body2">
          {intl('O aplicativo {app} está solicitando acesso somente leitura ao NeoDash por meio do servidor NeoDash MCP ({url}).', {
            app: appName,
            url: MCP_SERVER_URL
          })}
        </p>

        <h5 className="subtitle2 fw-bold">{intl('O que ele pode fazer')}</h5>
        <ul className="body2">
          <li>{intl('Ler workspaces, dashboards, métricas, dimensões e resultados de consultas que o seu usuário NeoDash já está autorizado a ver. Ele nunca vê mais do que você.')}</li>
          <li>{intl('Devolver links de volta pro NeoDash, para que toda resposta possa ser conferida na fonte.')}</li>
          <li>{intl('Ele não pode gravar, apagar ou alterar dados, usuários ou configurações. Não acessa workspaces de outros usuários.')}</li>
        </ul>

        <h5 className="subtitle2 fw-bold">{intl('Como está protegido')}</h5>
        <ul className="body2">
          <li>{intl('A autorização usa OAuth 2.0 / OIDC conforme a especificação MCP vigente (2026-07-28). Sua senha do NeoDash nunca é compartilhada com o assistente.')}</li>
          <li>{intl('Todo o tráfego é criptografado em trânsito (TLS/HTTPS) entre o assistente, o servidor NeoDash MCP e o NeoDash.')}</li>
          <li>{intl('O token emitido hoje é pessoal, restrito ao seu usuário e revogável a qualquer momento desconectando o servidor no Assistente.')}</li>
          <li>{intl('O NeoDash não vende, não compartilha com terceiros e não usa seus dados para treinar modelos de IA.')}</li>
        </ul>

        <h5 className="subtitle2 fw-bold">{intl('Sua responsabilidade')}</h5>
        <p className="body2">
          {intl('Ao autorizar, você confirma que {app} é uma ferramenta de IA homologada pelas políticas de TI/segurança da sua empresa. Depois que o dado é entregue ao assistente, ele passa a ser processado sob os termos daquele provedor, fora do controle do NeoDash. O NeoDash não se responsabiliza por como o assistente armazena, retém ou reutiliza dados após saírem do NeoDash.', {
            app: appName
          })}
        </p>

        <AckS className="body2">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={event => setAcknowledged(event.target.checked)}
          />
          <span>
            {intl('Entendo que apenas os dados que meu usuário está autorizado a ver serão expostos, e que sou responsável por usar um assistente homologado pela minha organização.')}
          </span>
        </AckS>

        <FullTermsS className="body2">
          <p className="fw-bold mb-0">{intl('Acesse os termos completos:')}</p>
          <p className="mb-0">
            {intl('Termos de uso')}:{' '}
            <a href={termsUrl} target="_blank" rel="noreferrer">{termsUrl}</a>
          </p>
          <p className="mb-0">
            {intl('Privacidade')}:{' '}
            <a href={privacyUrl} target="_blank" rel="noreferrer">{privacyUrl}</a>
          </p>
        </FullTermsS>
      </BodyS>

      <FooterS>
        <Button className="flex-grow-1" color="gray" onClick={cancel}>
          {intl('Cancelar')}
        </Button>
        <Button
          className="flex-grow-1"
          color="primary"
          disabled={!acknowledged}
          onClick={onAccept}
        >
          {intl('Aceitar')}
        </Button>
      </FooterS>
    </ModalS>
  )
}

export default TermsModal
